import interact from 'interactjs';
import { DEFAULT_RELEASE_PROFILE, getReleaseDurationMs, readDrawerEdgeMaxPx, shouldCloseByDistanceOrFlick } from '../../core/drag';
import {
    DEFAULT_DRAG_BASE,
    DEFAULT_EDGE_BASE,
    readNumberSetting,
    readOptionalNumberSetting,
} from '../../core/motion';
import { updateHamburger, findHamburgers, bindHamburger } from './hamburger';
import './hamburger.css';
import './styles.css';

const DRAWER_DRAG_SELECTOR = '.drawer > .drawer-side [data-draggable="true"]';
const DRAWER_BOUND_KEY = 'drawer';
let drawerBound = false;

export function initDrawerCustom(): void {
    if (drawerBound) return;
    drawerBound = true;
    document.addEventListener('ui:update', () => {
        initDrawerDraggables(document);
    });
}

export function initDrawerDraggables(scope: ParentNode): void {
    const targets = Array.from(scope.querySelectorAll<HTMLElement>(DRAWER_DRAG_SELECTOR));
    targets.forEach((el) => {
        if (el.dataset.draggableBound === DRAWER_BOUND_KEY) return;
        el.dataset.draggableBound = DRAWER_BOUND_KEY;
        const interactable = bindDrawerLikeDrag(el);
        el.addEventListener('remove', () => interactable.unset(), { once: true });
    });
}

export function bindDrawerLikeDrag(el: HTMLElement) {
    const drawer = el.closest('.drawer') as HTMLElement | null;
    const toggle = drawer?.querySelector<HTMLInputElement>(':scope > .drawer-toggle') ?? null;
    const drawerSide = drawer?.querySelector<HTMLElement>(':scope > .drawer-side') ?? null;
    const overlay = drawerSide?.querySelector<HTMLElement>(':scope > .drawer-overlay') ?? null;
    const isEnd = !!drawer?.classList.contains('drawer-end');
    const isNegativeCloseDirection = !isEnd;
    const edgeMaxPx = readDrawerEdgeMaxPx();
    const config = readDrawerDragConfig(drawer);
    const hamburgers = drawerSide ? findHamburgers(drawerSide) : [];
    if (drawerSide && toggle) {
        hamburgers.forEach(btn => bindHamburger(btn, drawerSide));
    }
    let dxTotal = 0;
    let startMs = 0;
    let lastMoveMs = 0;
    let maxCloseTravel = 0;
    let dragging = false;
    let dragMoved = false;
    let dragActivateOffsetX = 0;
    let panelSizePx = 1;
    let currentDragX = 0;
    let lastDrawX: number | null = null;
    let lastOverlayOpacity: number | null = null;
    let releaseTimer: number | null = null;
    let frameId: number | null = null;
    let pendingX = 0;
    let pendingOverlay = 1;
    let currentSpeed = 0;
    let dragStartRawX = 0;

    const clearStyles = (options?: { keepOverlayOpacity?: boolean }): void => {
        const keepOverlayOpacity = options?.keepOverlayOpacity === true;
        if (frameId !== null) {
            cancelAnimationFrame(frameId);
            frameId = null;
        }
        el.style.removeProperty('will-change');
        el.style.removeProperty('transition');
        el.style.removeProperty('transform');
        el.style.removeProperty('opacity');
        if (overlay) {
            overlay.style.removeProperty('will-change');
            overlay.style.removeProperty('transition');
            if (!keepOverlayOpacity) overlay.style.removeProperty('opacity');
        }
        if (releaseTimer !== null) {
            window.clearTimeout(releaseTimer);
            releaseTimer = null;
        }
    };

    const bindToggleSync = (target: HTMLInputElement | null): void => {
        if (!target || target.dataset.dragSyncBound === 'true') return;
        target.dataset.dragSyncBound = 'true';
        target.addEventListener('change', () => {
            if (dragging) return;
            if (target.checked) {
                if (drawerSide) drawerSide.style.removeProperty('display');
                clearStyles();
            }
        });
    };

    if (toggle && drawerSide && drawer) {
        const id = toggle.id;
        if (id && drawer.dataset.drawerOpenGuardBound !== 'true') {
            drawer.dataset.drawerOpenGuardBound = 'true';
            document.addEventListener(
                'click',
                (event) => {
                    const target = event.target;
                    if (!(target instanceof Element)) return;
                    const opener = target.closest(`label[for="${id}"]`);
                    if (!opener) return;
                    if (toggle.checked) return;
                    drawerSide.style.removeProperty('display');
                },
                true
            );
        }
    }

    bindToggleSync(toggle);

    const onStart = (): void => {
        if (releaseTimer !== null) {
            window.clearTimeout(releaseTimer);
            releaseTimer = null;
        }
        if (frameId !== null) {
            cancelAnimationFrame(frameId);
            frameId = null;
        }
        dragging = true;
        dxTotal = 0;
        dragMoved = false;
        dragActivateOffsetX = 0;
        dragStartRawX = 0;
        startMs = performance.now();
        lastMoveMs = startMs;
        currentSpeed = 0;
        maxCloseTravel = 0;
        panelSizePx = Math.max(1, el.offsetWidth);
        currentDragX = 0;
        pendingX = 0;
        pendingOverlay = 1;
        lastDrawX = null;
        lastOverlayOpacity = null;
        hamburgers.forEach(btn => { btn.dataset.hamburgerDragging = 'true'; });
        el.style.willChange = 'transform, opacity';
        el.style.transition = 'none';
        if (overlay) {
            overlay.style.willChange = 'opacity';
            overlay.style.transition = 'none';
            overlay.style.opacity = '1';
        }
    };

    const onMove = (event: Interact.DragEvent): void => {
        if (!dragging) return;
        dxTotal += event.dx;
        const rawPrimary = dxTotal;
        if (!dragMoved) dragActivateOffsetX = rawPrimary;
        dragMoved = true;
        const activePrimary = rawPrimary - dragActivateOffsetX;
        if (dragStartRawX === 0) dragStartRawX = activePrimary;
        const isClosingDirection = isNegativeCloseDirection ? activePrimary < 0 : activePrimary > 0;
        const resistedPrimary = Math.max(
            -edgeMaxPx,
            Math.min(edgeMaxPx, activePrimary * config.edgeResistance)
        );
        const primary = isClosingDirection
            ? isNegativeCloseDirection
                ? Math.min(0, activePrimary)
                : Math.max(0, activePrimary)
            : resistedPrimary;
        const closeTravel = isClosingDirection ? Math.abs(primary) : 0;
        if (isClosingDirection && closeTravel > maxCloseTravel) maxCloseTravel = closeTravel;
        const x = primary;
        const overlayOpacity = Math.max(0, 1 - closeTravel / panelSizePx);
        currentDragX = x;

        // 瞬間速度（直前フレームとの差分）で計算
        const now = performance.now();
        const dt = Math.max(1, now - lastMoveMs);
        currentSpeed = Math.abs(event.dx) / dt;
        lastMoveMs = now;

        // pending 値を更新して rAF でバッチ描画
        pendingX = x;
        pendingOverlay = overlayOpacity;
        if (frameId !== null) return;
        frameId = requestAnimationFrame(() => {
            frameId = null;
            if (lastDrawX === null || Math.abs(lastDrawX - pendingX) > 0.01) {
                el.style.transform = `translate3d(${pendingX}px, 0, 0)`;
                lastDrawX = pendingX;
            }
            if (overlay && (lastOverlayOpacity === null || Math.abs(lastOverlayOpacity - pendingOverlay) > 0.002)) {
                overlay.style.opacity = String(pendingOverlay);
                lastOverlayOpacity = pendingOverlay;
            }
            hamburgers.forEach(btn => updateHamburger(btn, pendingOverlay));
        });
    };

    const onEnd = (): void => {
        if (!dragging) return;
        // pending rAF をキャンセルして最終位置を即時反映
        if (frameId !== null) {
            cancelAnimationFrame(frameId);
            frameId = null;
            if (lastDrawX === null || Math.abs(lastDrawX - pendingX) > 0.01) {
                el.style.transform = `translate3d(${pendingX}px, 0, 0)`;
                lastDrawX = pendingX;
            }
            if (overlay && (lastOverlayOpacity === null || Math.abs(lastOverlayOpacity - pendingOverlay) > 0.002)) {
                overlay.style.opacity = String(pendingOverlay);
                lastOverlayOpacity = pendingOverlay;
            }
        }
        const x = currentDragX;
        const distance = Math.abs(x);
        const closeDistanceThreshold = panelSizePx / 2;
        const speed = currentSpeed; // 瞬間速度を使用
        const shouldCloseByThreshold = shouldCloseByDistanceOrFlick(
            distance,
            closeDistanceThreshold,
            speed,
            config.closeSpeedThreshold,
            config.flickMinDistancePx
        );
        const adaptiveDurationMs = getReleaseDurationMs(speed);
        const closeDurationMs = config.closeDurationMs ?? adaptiveDurationMs;

        const isClosingDirection = isNegativeCloseDirection ? x < 0 : x > 0;
        const returnDurationMs = DEFAULT_RELEASE_PROFILE.normal;
        const closeTravel = Math.abs(x);
        const isReturnedFromPeak = closeTravel < maxCloseTravel;

        if (!isClosingDirection || !shouldCloseByThreshold || isReturnedFromPeak) {
            el.style.transition = `transform ${returnDurationMs}ms ease`;
            el.style.transform = 'translate3d(0, 0, 0)';
            if (overlay) {
                overlay.style.transition = `opacity ${returnDurationMs}ms ease`;
                overlay.style.opacity = '1';
            }
            hamburgers.forEach(btn => { delete btn.dataset.hamburgerDragging; });
            releaseTimer = window.setTimeout(() => {
                dragging = false;
                clearStyles();
            }, returnDurationMs + 20);
            return;
        }

        const outX = isNegativeCloseDirection ? -window.innerWidth : window.innerWidth;
        el.style.transition = `transform ${closeDurationMs}ms ease, opacity ${closeDurationMs}ms ease`;
        el.style.transform = `translate3d(${outX}px, 0, 0)`;
        el.style.opacity = '0';
        if (overlay) {
            overlay.style.transition = `opacity ${closeDurationMs}ms ease`;
            overlay.style.opacity = '0';
        }

        hamburgers.forEach(btn => { delete btn.dataset.hamburgerDragging; });
        releaseTimer = window.setTimeout(() => {
            if (drawerSide) drawerSide.style.setProperty('display', 'none', 'important');
            if (toggle) toggle.checked = false;
            hamburgers.forEach(btn => updateHamburger(btn, 0));
            window.requestAnimationFrame(() => {
                dragging = false;
                clearStyles({ keepOverlayOpacity: true });
            });
        }, closeDurationMs + 20);
    };

    const interactable = interact(el);
    interactable.styleCursor(false);
    return interactable.draggable({
        startAxis: 'x',
        lockAxis: 'x',
        listeners: {
            start() {
                onStart();
            },
            move(event) {
                onMove(event);
            },
            end() {
                onEnd();
            },
        },
    });
}

function readDrawerDragConfig(drawer: HTMLElement | null) {
    const dataset = drawer?.dataset;
    return {
        closeSpeedThreshold: readNumberSetting(
            dataset?.drawerCloseSpeedThreshold,
            DEFAULT_DRAG_BASE.closeSpeedThreshold,
            0
        ),
        flickMinDistancePx: readNumberSetting(
            dataset?.drawerFlickMinDistancePx,
            DEFAULT_DRAG_BASE.flickMinDistancePx,
            0
        ),
        returnDurationMs: readOptionalNumberSetting(dataset?.drawerReturnDurationMs, 0),
        closeDurationMs: readOptionalNumberSetting(dataset?.drawerCloseDurationMs, 0),
        edgeResistance: readNumberSetting(dataset?.drawerEdgeResistance, DEFAULT_EDGE_BASE.resistance, 0),
    };
}
